import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { getSubscriptionKeyList } from "api/subscriptionAPI";
import {
  GetSubscriptionKeyResponse,
  SelectedRowAccountInfo,
  SUBSCRIPTION_TYPE,
  SubscriptionKeyListReqeust,
  SubscriptionKeyListResponse,
  SubscriptionKeyListType,
  SubscriptonKeyListSummary,
  ChangeWidthColumn,
  SubscriptionPageOutletContext,
} from "api/interfaces/subscription.interface";
import { TableColumnsType } from "antd";
import { SubscriptionKeyColumn } from "pages/subscription/lyve/list/SubscriptionKeyColumn";
import ExpiredDate from "pages/subscription/modal/common/ExpiredDate";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { useKeyType } from "./useKeyType";
import { formatNumber, formatUppercaseOnly } from "utils/functions";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { useQueryKey } from "utils/queryUtil";
import {
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useOutletContext } from "react-router-dom";
import useStompDebouncedRefetch from "hook/useStompDebouncedRefetch";

export interface UseKeyTableProps {
  selectedRowAccount?: SelectedRowAccountInfo;
  selectedRowAccountId?: string;
  queryInfo: SubscriptionKeyListReqeust;
  setQueryInfo: Dispatch<SetStateAction<SubscriptionKeyListReqeust>>;
}

export const initialQueryKeyInfo: SubscriptionKeyListReqeust = {
  total: 0,
  pageNum: 0,
  pageLimit: 20,
  keyword: "",
  sortType: "createdAt",
  sortDirection: SORT_DIRECTION.DESC,
  dateType: "createdAt",
};
export function useKeyTable({
  selectedRowAccount,
  selectedRowAccountId,
  queryInfo,
  setQueryInfo,
}: UseKeyTableProps) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryKey = useQueryKey({ ...queryInfo });

  const { keyTypeOptions, durationMonthOptions } = useKeyType();
  const { onChangePage, onHeaderCell, onClickFilterTitle } =
    useOutletContext<SubscriptionPageOutletContext>();

  const accountId = useMemo(() => {
    if (selectedRowAccountId) {
      return selectedRowAccountId;
    }
    return selectedAccount.accountId;
  }, [selectedRowAccountId, selectedAccount.accountId]);

  const useQueryKeyList = useQuery(
    ["getSubscriptionKeyList", queryKey, accountId],
    () =>
      getSubscriptionKeyList({
        accountId,
        payload: queryInfo,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!accountId,
      onSuccess: (res: GetSubscriptionKeyResponse) => {
        if (res.page) {
          setQueryInfo((query) => {
            return {
              ...query,
              ...res.page,
            };
          });
        }
      },
      onError: (_e: any) => {},
    }
  );
  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
        subscriptionGenerateUpdated:
          state.subscriptionStomp.subscriptionGenerateUpdated,
        subscriptionRegisterUpdated:
          state.subscriptionStomp.subscriptionRegisterUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "subscriptionActivatedUpdated",
      "subscriptionGenerateUpdated",
      "subscriptionRegisterUpdated",
    ],

    refetch: useQueryKeyList.refetch,
  });

  const changeWidthColumn = (
    columns: TableColumnsType<SubscriptionKeyListType>,
    changes: ChangeWidthColumn[]
  ): TableColumnsType<SubscriptionKeyListType> => {
    return columns.map((column) => {
      if ("dataIndex" in column && column.dataIndex) {
        const matchingChange: ChangeWidthColumn | undefined = changes.find(
          (change) => change.dataIndex === column.dataIndex
        );
        if (matchingChange) {
          Object.keys(matchingChange).forEach((key) => {
            (column as any)[key] = (matchingChange as ChangeWidthColumn)[
              key as keyof ChangeWidthColumn
            ];
          });
        }
      }
      return column;
    }) as TableColumnsType<SubscriptionKeyListType>;
  };

  const subscriptionKeyList: SubscriptionKeyListResponse[] = useMemo(() => {
    if (
      useQueryKeyList.isSuccess &&
      useQueryKeyList.data?.result &&
      useQueryKeyList.data.result.list &&
      useQueryKeyList.data.error === 0
    ) {
      return useQueryKeyList.data.result.list;
    }
    return [];
  }, [useQueryKeyList]);

  const subscriptionKeySummary: SubscriptonKeyListSummary = useMemo(() => {
    if (
      useQueryKeyList.isSuccess &&
      useQueryKeyList.data?.result &&
      useQueryKeyList.data.result.summary &&
      useQueryKeyList.data.error === 0
    ) {
      return useQueryKeyList.data.result.summary;
    }
    return { expireSoon: 0, expired: 0 };
  }, [useQueryKeyList]);

  const commonKeyColumns: TableColumnsType<SubscriptionKeyListType> = [
    {
      title: "Key",
      dataIndex: "dwKey",
      render: (value, row) => <SubscriptionKeyColumn subscriptionKey={value} />,
      width: 250,
    },
    {
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={keyTypeOptions}
          onClick={(value: string | undefined) => {
            onClickFilterTitle(value, "type");
          }}
          data-testid="hook-subscription-table-column-filter-title-click-filter-title"
        />
      ),

      dataIndex: "type",
      render: (value, row) => {
        return formatUppercaseOnly(value);
      },
      width: 90,
      responsive: ["md"],
    },
    {
      title: "Purchased",
      dataIndex: "purchased",
      key: "purchased",
      render: (value, row) => {
        let unit = "CH";
        if (row.type === SUBSCRIPTION_TYPE.STORAGE) {
          unit = "TB";
        }
        return `${formatNumber(value)} ${unit}`;
      },
      width: 90,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "duration"),
      key: "duration",
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        const duration = (
          durationMonthOptions(row.type as SUBSCRIPTION_TYPE, true).find(
            (opt) => opt.value === String(value)
          )?.label || "-"
        ).split("(");
        const durationLabel = duration[0];
        const durationDays = duration[1];
        return (
          <div className="duration-column">
            <span className="duration-label">{durationLabel}</span>{" "}
            {durationDays && (
              <span className="duration-days">({durationDays}</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Price",
      dataIndex: "actualPrice",
      sorter: true,
      key: "actualPrice",
      sortOrder: changeSortOrderAntd(queryInfo, "actualPrice"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        if (row.isFreeKey) {
          return "Free";
        }
        return value ? `$${formatNumber(value.toFixed(2))}` : "-";
      },
      hidden: selectedAccount.accountLevel !== LEVEL_TYPE.DW,
      width: 100,
      responsive: ["lg"],
    },

    {
      title: "Created Date",
      dataIndex: "createdAt",
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(queryInfo, "createdAt"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        return (
          <>
            {getAccountTimeFormatDate(value, selectedAccount)}
            <br />
            {row.user?.name || "System"}
          </>
        );
      },
      responsive: ["lg"],
    },

    {
      title: "Registered Date",
      dataIndex: "registerDate",
      sorter: true,
      key: "registerDate",
      sortOrder: changeSortOrderAntd(queryInfo, "registerDate"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        if (!value) {
          return "-";
        }
        return getAccountTimeFormatDate(value, selectedAccount);
      },
      responsive:
        selectedAccount.accountLevel === LEVEL_TYPE.DW ? ["xxl"] : ["lg"],
      width: 120,
    },

    {
      title: "Activated",
      dataIndex: "activateDate",
      sorter: true,
      key: "activateDate",
      sortOrder: changeSortOrderAntd(queryInfo, "activateDate"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => getAccountTimeFormatDate(value, selectedAccount),
    },
    {
      title: "Expiration",
      key: "expireDate",
      dataIndex: "expireDate",
      render: (value, row) => (
        <ExpiredDate
          status={row.status}
          expiredDate={value}
          extendTargetKey={row.extendTargetKey}
        />
      ),
      sortOrder: changeSortOrderAntd(queryInfo, "expireDate"),
      width: 110,
      sorter: true,
      onHeaderCell: onHeaderCell,
    },
  ];

  return {
    subscriptionKeyList,
    subscriptionKeySummary,
    useQueryKeyList,
    commonKeyColumns,
    onChangePage,
    onClickFilterTitle,
    changeWidthColumn,
    onHeaderCell,
  };
}
